/**
 * Crease-edge extraction for CSG (Manifold) output meshes — shared by the
 * floor 3D mesh and the wall-sketch 3D skin outline overlays. Standalone
 * module (THREE core only) so it stays importable from jest specs.
 */
import * as THREE from 'three';

/** Tolerance (metres) for treating two vertices as the same point. CSG
 * SUBTRACTION drifts positions by a few mm, so 5 mm comfortably clusters the
 * copies of a shared corner without merging genuinely distinct geometry. */
const WELD_TOLERANCE = 0.005;

/**
 * Minimum distance (metres) a face must extend PERPENDICULAR to an edge for
 * it to justify drawing that edge as a crease. The partition's snap grid and
 * junction quantisation leave sub-millimetre step strips in the CSG result
 * whose side faces stand at ~90° to the walls; outlining those draws long
 * phantom lines across visually flat faces (and truly degenerate slivers
 * with noise normals are the extreme case, reach ≈ 0). 2 mm is well above
 * every quantisation artifact (0.1 mm snap, ~1 mm hull shaves) and far below
 * any real wall feature (thicknesses start at several cm).
 */
const MIN_FEATURE_REACH = 0.002;

/**
 * Debug helper: emit EVERY triangle edge in the geometry, no coplanarity
 * filtering. Used by the skeleton debug "Show All Triangles" toggle to
 * reveal the raw mesh triangulation. Returns a flat
 * `[x,y,z, x,y,z, ...]` array of edge endpoints.
 */
export function extractAllTriangleEdges(geometry: THREE.BufferGeometry): number[] {
  const pos = geometry.getAttribute('position');
  if (!pos) return [];
  const idx = geometry.getIndex();
  const idxArr = idx ? (idx.array as Uint32Array | Uint16Array) : null;
  const triCount = idxArr ? idxArr.length / 3 : Math.floor(pos.count / 3);
  const vertOf = (t: number, corner: number): number => (idxArr ? idxArr[t * 3 + corner] : t * 3 + corner);
  const out: number[] = [];
  const tmp = new THREE.Vector3();
  const pushVert = (i: number) => {
    tmp.fromBufferAttribute(pos, i);
    out.push(tmp.x, tmp.y, tmp.z);
  };
  for (let t = 0; t < triCount; t++) {
    const a = vertOf(t, 0);
    const b = vertOf(t, 1);
    const c = vertOf(t, 2);
    pushVert(a);
    pushVert(b);
    pushVert(b);
    pushVert(c);
    pushVert(c);
    pushVert(a);
  }
  return out;
}

/**
 * Find crease edges on a (possibly non-indexed, drifted) BufferGeometry. An
 * edge is a crease — and therefore rendered — unless its two adjacent faces
 * are coplanar (the surface is flat across it).
 *
 * The hard part is *face adjacency*. CSG output is non-indexed and drifted, so
 * two triangles that share an edge in space do NOT share vertex indices, and
 * the obvious fixes (snap-to-grid + `mergeVertices`) are unreliable: a shared
 * corner whose two copies straddle a grid line fails to weld, the edge looks
 * unpaired, and gets dropped — that was the "random missing edges" bug. We
 * instead reconstruct adjacency directly by clustering coincident vertices
 * within {@link WELD_TOLERANCE} using a spatial hash that also checks
 * neighbouring buckets, which has no grid-boundary artifact.
 *
 * Coplanarity uses |n₁ · n₂| so antiparallel normals count as flat too: CSG
 * can emit inconsistently-wound triangles on one logical surface, and we don't
 * want every such internal edge drawn. `thresholdDeg` is the angular tolerance
 * — faces diverging by more than it keep their shared edge.
 *
 * Returns a flat `[x,y,z, x,y,z, ...]` array of crease-edge endpoints, ready
 * to feed into `LineSegmentsGeometry.setPositions`.
 */
export function computeCreaseEdges(geometry: THREE.BufferGeometry, thresholdDeg: number): number[] {
  const pos = geometry.getAttribute('position');
  if (!pos) return [];

  // Cluster coincident vertices by proximity → a stable id per unique point,
  // independent of vertex ordering (Manifold output is welded already, but a
  // non-indexed three.js geometry isn't — this handles both).
  const clusterIds = clusterVerticesByProximity(pos, WELD_TOLERANCE);

  // Triangle vertex indices: honour the index buffer when present (Manifold's
  // result is indexed), else treat every 3 sequential vertices as a triangle.
  const idx = geometry.getIndex();
  const idxArr = idx ? (idx.array as Uint32Array | Uint16Array) : null;
  const triCount = idxArr ? idxArr.length / 3 : Math.floor(pos.count / 3);
  const vertOf = (t: number, corner: number): number => (idxArr ? idxArr[t * 3 + corner] : t * 3 + corner);

  // Per-triangle face normals + cross-product magnitude (2× area). CSG
  // triangulation emits DEGENERATE slivers whose cross product is numeric
  // noise ("their normal points anywhere"), and the partition's 0.1 mm snap
  // grid leaves sub-millimetre step strips whose side faces stand at ~90° to
  // the walls. Both classes would otherwise draw long phantom outline lines
  // across perfectly flat faces; they're filtered per-edge below via the
  // SURFACE-REGION reach test.
  const normals = new Float32Array(triCount * 3);
  const degenerate = new Uint8Array(triCount);
  const vA = new THREE.Vector3();
  const vB = new THREE.Vector3();
  const vC = new THREE.Vector3();
  const ab = new THREE.Vector3();
  const ac = new THREE.Vector3();
  const bcv = new THREE.Vector3();
  const fn = new THREE.Vector3();
  for (let t = 0; t < triCount; t++) {
    vA.fromBufferAttribute(pos, vertOf(t, 0));
    vB.fromBufferAttribute(pos, vertOf(t, 1));
    vC.fromBufferAttribute(pos, vertOf(t, 2));
    ab.subVectors(vB, vA);
    ac.subVectors(vC, vA);
    bcv.subVectors(vC, vB);
    fn.crossVectors(ab, ac);
    const crossLen = fn.length();
    const longestEdge = Math.max(ab.length(), ac.length(), bcv.length());
    // Height over longest edge below 10 µm → the normal is float noise.
    degenerate[t] = longestEdge > 0 && crossLen / longestEdge > 1e-5 ? 0 : 1;
    const l = crossLen || 1;
    normals[t * 3] = fn.x / l;
    normals[t * 3 + 1] = fn.y / l;
    normals[t * 3 + 2] = fn.z / l;
  }

  // Undirected edge → adjacent-triangle list, keyed by sorted cluster pair so
  // triangles sharing an edge in space are matched despite distinct indices.
  // `repVert` remembers one original vertex index per cluster for emitting
  // the final positions.
  const edgeMap = new Map<string, number[]>();
  const repVert = new Map<number, number>();
  const neighbours: number[][] = Array.from({ length: triCount }, () => []);
  for (let t = 0; t < triCount; t++) {
    const v0 = vertOf(t, 0);
    const v1 = vertOf(t, 1);
    const v2 = vertOf(t, 2);
    const verts = [v0, v1, v2];
    const c = [clusterIds[v0], clusterIds[v1], clusterIds[v2]];
    for (let j = 0; j < 3; j++) {
      const ca = c[j];
      const cb = c[(j + 1) % 3];
      if (!repVert.has(ca)) repVert.set(ca, verts[j]);
      // A side whose endpoints weld into the same cluster is a POINT after
      // welding, not an edge — emitting it would draw zero-length dots.
      if (ca === cb) continue;
      const key = ca < cb ? `${ca}|${cb}` : `${cb}|${ca}`;
      const list = edgeMap.get(key);
      if (list) list.push(t);
      else edgeMap.set(key, [t]);
    }
  }
  for (const tris of edgeMap.values()) {
    for (let i = 0; i < tris.length; i++) {
      for (let j = 0; j < tris.length; j++) {
        if (i !== j) neighbours[tris[i]].push(tris[j]);
      }
    }
  }

  const cosThreshold = Math.cos((thresholdDeg * Math.PI) / 180);

  /**
   * Character of the SURFACE REGION on one side of an edge: flood-fill from
   * the adjacent triangle across coplanar (within threshold) shared edges,
   * treating degenerate triangles as transparent connectors. Returns the
   * region's representative normal (first non-degenerate triangle found) and
   * how far the region reaches perpendicular from the edge's line.
   *
   * Judging the REGION rather than the single adjacent triangle matters in
   * both directions: a flat face's fan triangulation can put a sliver along a
   * real corner (the sliver alone reaches <2 mm but its surface reaches the
   * full face — the corner must stay outlined), while a sub-millimetre step
   * strip's whole surface stays within a hair of the edge no matter how long
   * it is (its creases must be hidden).
   */
  const sideRegion = (seed: number, ax: number, ay: number, az: number, dx: number, dy: number, dz: number): { nIdx: number; reach: number } => {
    const queue = [seed];
    const seen = new Set<number>([seed]);
    let reach = 0;
    let nIdx = -1;
    for (let q = 0; q < queue.length; q++) {
      const t = queue[q];
      if (nIdx === -1 && !degenerate[t]) nIdx = t;
      for (let corner = 0; corner < 3; corner++) {
        const v = vertOf(t, corner);
        const px = pos.getX(v) - ax;
        const py = pos.getY(v) - ay;
        const pz = pos.getZ(v) - az;
        const along = px * dx + py * dy + pz * dz;
        const dist = Math.sqrt(Math.max(0, px * px + py * py + pz * pz - along * along));
        if (dist > reach) reach = dist;
      }
      if (nIdx !== -1 && reach > MIN_FEATURE_REACH) break;
      for (const u of neighbours[t]) {
        if (seen.has(u)) continue;
        // Cross into coplanar terrain only; degenerate triangles connect
        // without constraining (their normal is meaningless).
        let ok = degenerate[t] === 1 || degenerate[u] === 1;
        if (!ok) {
          const dot = normals[t * 3] * normals[u * 3] + normals[t * 3 + 1] * normals[u * 3 + 1] + normals[t * 3 + 2] * normals[u * 3 + 2];
          ok = Math.abs(dot) >= cosThreshold;
        }
        if (ok) {
          seen.add(u);
          queue.push(u);
        }
      }
    }
    return { nIdx, reach };
  };

  const out: number[] = [];
  for (const [key, tris] of edgeMap) {
    const sep = key.indexOf('|');
    const ca = parseInt(key.slice(0, sep), 10);
    const cb = parseInt(key.slice(sep + 1), 10);
    const va = repVert.get(ca);
    const vb = repVert.get(cb);
    if (va === undefined || vb === undefined) continue;
    const ax = pos.getX(va);
    const ay = pos.getY(va);
    const az = pos.getZ(va);
    let dx = pos.getX(vb) - ax;
    let dy = pos.getY(vb) - ay;
    let dz = pos.getZ(vb) - az;
    const dl = Math.hypot(dx, dy, dz) || 1;
    dx /= dl;
    dy /= dl;
    dz /= dl;

    // The edge is outlined when two of its side REGIONS diverge by more than
    // the threshold and both reach at least MIN_FEATURE_REACH away from the
    // edge; a boundary edge (single side) needs its one region to qualify.
    const sides = tris.map((t) => sideRegion(t, ax, ay, az, dx, dy, dz));
    let isCrease = false;
    if (sides.length < 2) {
      isCrease = sides.length === 1 && sides[0].nIdx !== -1 && sides[0].reach > MIN_FEATURE_REACH;
    } else {
      outer: for (let i = 0; i < sides.length - 1; i++) {
        for (let j = i + 1; j < sides.length; j++) {
          const si = sides[i];
          const sj = sides[j];
          if (si.nIdx === -1 || sj.nIdx === -1) continue;
          if (si.reach <= MIN_FEATURE_REACH || sj.reach <= MIN_FEATURE_REACH) continue;
          const ni = si.nIdx * 3;
          const nj = sj.nIdx * 3;
          const dot =
            normals[ni] * normals[nj] + normals[ni + 1] * normals[nj + 1] + normals[ni + 2] * normals[nj + 2];
          if (Math.abs(dot) < cosThreshold) {
            isCrease = true;
            break outer;
          }
        }
      }
    }
    if (!isCrease) continue;
    out.push(ax, ay, az, pos.getX(vb), pos.getY(vb), pos.getZ(vb));
  }
  return out;
}

/**
 * Assign each vertex a cluster id, merging vertices within `tol` of one
 * another. Uses a spatial hash bucketed at `2·tol` and probes the 27
 * surrounding buckets, so points near a bucket boundary still match a
 * representative in the neighbouring bucket — the property a fixed snap-grid
 * lacks. Returns an `Int32Array` of cluster ids, one per vertex.
 */
function clusterVerticesByProximity(pos: THREE.BufferAttribute | THREE.InterleavedBufferAttribute, tol: number): Int32Array {
  const cell = tol * 2;
  const ids = new Int32Array(pos.count);
  // bucket key → representatives in that bucket
  const buckets = new Map<string, Array<{ id: number; x: number; y: number; z: number }>>();
  let nextId = 0;

  for (let v = 0; v < pos.count; v++) {
    const x = pos.getX(v);
    const y = pos.getY(v);
    const z = pos.getZ(v);
    const bx = Math.floor(x / cell);
    const by = Math.floor(y / cell);
    const bz = Math.floor(z / cell);

    let found = -1;
    for (let dx = -1; dx <= 1 && found < 0; dx++) {
      for (let dy = -1; dy <= 1 && found < 0; dy++) {
        for (let dz = -1; dz <= 1 && found < 0; dz++) {
          const reps = buckets.get(`${bx + dx}_${by + dy}_${bz + dz}`);
          if (!reps) continue;
          for (const r of reps) {
            if (Math.abs(r.x - x) <= tol && Math.abs(r.y - y) <= tol && Math.abs(r.z - z) <= tol) {
              found = r.id;
              break;
            }
          }
        }
      }
    }

    if (found < 0) {
      found = nextId++;
      const key = `${bx}_${by}_${bz}`;
      const reps = buckets.get(key);
      if (reps) reps.push({ id: found, x, y, z });
      else buckets.set(key, [{ id: found, x, y, z }]);
    }
    ids[v] = found;
  }
  return ids;
}
